import { createDeploymentAction, getDeploymentJobAction } from 'store/actions/deployments.action'
import { notifyToast } from 'utils/responseUtils'
import { deploymentStatuses, runningStatuses, waitingStatuses } from 'utils/constants'
import { authHandler } from 'store/actions/auth.action'

export const runDeploymentService =
  (
    selectedModel,
    saveModelProgress,
    t,
    setGenerationData,
    generationData,
    setActiveMedia = () => {},
    callback = () => {},
    handleDeploymentLoading = () => {},
    errorCallback = () => {}
  ) =>
  async (dispatch) => {
    console.log('🚀 Starting deployment service with:', { selectedModel: selectedModel.name, generationData })
    handleDeploymentLoading(true, 1) // Start with 1% progress
    let args = {}
    const argsArray = Object.keys(selectedModel.args)
    argsArray.map((item) => {
      if (selectedModel?.args[item].value) args[item] = selectedModel?.args[item].value
      else {
        return
      }
    })

    const newGenerationId = generationData?.length ? generationData[generationData.length - 1].id + 1 : 0

    const updateGenerationData = (updateFn) =>
      setGenerationData((prevData) => prevData.map((gen) => (gen.id === newGenerationId ? updateFn(gen) : gen)))

    updateGenerationData((gen) => ({
      ...gen,
      loadingEnabled: true,
      loadingEnabledP: false,
    }))

    const payload = {
      name: selectedModel.name,
      model_id: selectedModel.id,
      args,
    }

    const handleDeploymentCompletion = (res) => {
      console.log('✅ Deployment completed:', res)
      updateGenerationData((gen) => ({
        ...gen,
        generatedResults: res.file,
        duration: res.job?.duration || res.duration,
        loadingEnabled: false,
        loadingEnabledP: false,
        deploymentId: res.deploymentId,
        progress: null,
      }))

      setActiveMedia(Array.isArray(res.file) ? res.file[0] : res.file)
      dispatch(notifyToast('success', t('model.ai-deployment-success')))
      // Pass both deployment ID and files to callback
      callback(res.deploymentId, res.file, res.job?.duration || res.duration)
      dispatch(authHandler())
      handleDeploymentLoading(false)
    }

    const handleDeploymentFailure = () => {
      console.log('❌ Deployment failed')
      errorCallback()
      dispatch(notifyToast('error', 'Unexpected Error, please try again later!'))
      updateGenerationData((gen) => ({
        ...gen,
        loadingEnabled: false,
        loadingEnabledP: false,
      }))
      if (saveModelProgress) {
        dispatch(saveModelProgress(null))
      }
      handleDeploymentLoading(false)
    }

    const monitorDeploymentProgress = async (res, startTimestamp) => {
      try {
        const payload = await getDeploymentJobAction(res.id)
        if (!payload) return

        if (!runningStatuses.includes(payload.status)) {
          startTimestamp = Date.now()
        }
        const progressPercentage = Math.min(((Date.now() - startTimestamp) / (payload.eta * 1000)) * 100, 100)

        console.log('📊 Progress update:', {
          status: payload.status,
          progressPercentage: Math.round(progressPercentage),
          eta: payload.eta,
        })

        // Pass progress percentage to handleDeploymentLoading
        handleDeploymentLoading(true, progressPercentage)

        updateGenerationData((gen) => ({
          ...gen,
          progress: {
            bar: progressPercentage,
            message: `${payload.status}...`,
            logs: (gen.progress?.logs || '') + (payload.logs || payload.docker_pull_logs || ''),
          },
        }))

        const progressData = {
          bar: progressPercentage,
          loading: true,
          result: null,
          tempModel: selectedModel,
        }

        if (saveModelProgress) {
          dispatch(saveModelProgress(progressData))
        }

        if (!payload.status || waitingStatuses.includes(payload.status)) {
          await new Promise((resolve) => setTimeout(resolve, 2000))
          await monitorDeploymentProgress(res, startTimestamp)
        } else if (payload.status === 'complete') {
          res.file = payload.files
          handleDeploymentCompletion(res)
        } else if (payload.status === 'failed') {
          handleDeploymentFailure()
        }
      } catch (error) {
        console.log('❌ Error in monitorDeploymentProgress:', error)
        handleDeploymentFailure()
      }
    }

    try {
      createDeploymentAction(JSON.stringify(payload), async (res) => {
        console.log('🎯 Deployment created:', res)

        // Handle immediate errors in response
        if (res?.error) {
          console.log('❌ Deployment creation failed with error:', res.error)
          handleDeploymentFailure()
          return
        }

        if (res?.status === 'complete' && res.file) {
          handleDeploymentCompletion(res)
        } else if (!res?.status || waitingStatuses.includes(res?.status)) {
          dispatch(
            notifyToast(
              res?.status && deploymentStatuses[res.status] ? 'success' : 'error',
              (res?.status && deploymentStatuses[res.status]) || 'Unexpected Error, please try again later!'
            )
          )
          updateGenerationData((gen) => ({
            ...gen,
            loadingEnabledP: true,
          }))
          monitorDeploymentProgress(res, Date.now())
        } else {
          handleDeploymentFailure()
        }
      })
    } catch (error) {
      console.log('❌ Error in deployment service:', error)
      handleDeploymentFailure()
    }

    setGenerationData((prev) => [
      ...prev,
      {
        id: generationData.length,
        loadingEnabled: false,
        loadingEnabledP: false,
        progress: { bar: 0, message: '', logs: '' },
        generatedResults: null,
        deploymentId: null,
      },
    ])
  }
